import React, { createContext, useContext, ReactNode } from "react";
import { ISystemData } from "../types/resource";

/**
 * Interface for auth tag data from API
 */
interface AuthTag {
  id: number;
  name: string;
  is_active?: boolean;
  [key: string]: any;
}

/**
 * Combined context interface for system and auth tag data
 */
interface AuthAndSystemContextProps {
  // System data
  allSystemData: ISystemData[];
  isSystemDataLoading: boolean;
  setAllSystemData: React.Dispatch<React.SetStateAction<ISystemData[]>>;

  // Auth tag data
  allAuthTagsData: AuthTag[];
  isAuthTagsLoading: boolean;
  setAllAuthTagsData: React.Dispatch<React.SetStateAction<AuthTag[]>>;
}

/**
 * Props interface for TagAndSystemProvider component
 */
interface TagAndSystemProviderProps {
  children: ReactNode;
}

// Create the combined AuthAndSystem context
const AuthAndSystemContext = createContext<
  AuthAndSystemContextProps | undefined
>(undefined);

/**
 * Combined TagAndSystemProvider component that provides centralized system and auth tag data management
 * Eliminates duplicate API calls by sharing data across components
 * Can selectively load system data, auth tag data, or both based on props
 */
export const TagAndSystemProvider: React.FC<TagAndSystemProviderProps> = ({
  children,
}) => {
  // Local state for auth tags data that can be updated by child components
  const [allAuthTagsData, setAllAuthTagsData] = React.useState<AuthTag[]>([]);

  // Local state for system data that can be updated by child components
  const [allSystemData, setAllSystemData] = React.useState<ISystemData[]>([]);

  // Auth tags data is now managed directly by pages that need it
  // No need to sync from hook since pages update context state directly

  const authAndSystemContextValue: AuthAndSystemContextProps = {
    // System data
    allSystemData: allSystemData,
    isSystemDataLoading: false, // Loading is now managed by individual pages
    setAllSystemData,

    // Auth tag data
    allAuthTagsData: allAuthTagsData,
    isAuthTagsLoading: false, // Loading is now managed by individual pages
    setAllAuthTagsData,
  };

  return (
    <AuthAndSystemContext.Provider value={authAndSystemContextValue}>
      {children}
    </AuthAndSystemContext.Provider>
  );
};

/**
 * Custom hook to access system data from the combined context
 * Provides the same interface as the original SystemContext
 * Returns default values if used outside of TagAndSystemProvider (graceful degradation)
 */
export const useSystemContext = () => {
  const context = useContext(AuthAndSystemContext);
  if (!context) {
    // Return safe default values when provider is not available
    // This allows components to use the hook without requiring the provider
    return {
      allSystemData: [],
      isSystemDataLoading: false,
      setAllSystemData: () => {
        // No-op function when provider is not available
        console.warn(
          "useSystemContext: setAllSystemData called outside of TagAndSystemProvider. This is a no-op."
        );
      },
    };
  }
  return {
    allSystemData: context.allSystemData,
    isSystemDataLoading: false, // Loading is now managed by individual pages
    setAllSystemData: context.setAllSystemData,
  };
};

/**
 * Custom hook to access auth tag data from the combined context
 * Provides the same interface as the original AuthTagContext
 * Returns default values if used outside of TagAndSystemProvider (graceful degradation)
 */
export const useAuthTagContext = () => {
  const context = useContext(AuthAndSystemContext);
  if (!context) {
    // Return safe default values when provider is not available
    // This allows components to use the hook without requiring the provider
    return {
      allAuthTagsData: [],
      isAuthTagsLoading: false,
      setAllAuthTagsData: () => {
        // No-op function when provider is not available
        console.warn(
          "useAuthTagContext: setAllAuthTagsData called outside of TagAndSystemProvider. This is a no-op."
        );
      },
    };
  }
  return {
    allAuthTagsData: context.allAuthTagsData,
    isAuthTagsLoading: false, // Loading is now managed by individual pages
    setAllAuthTagsData: context.setAllAuthTagsData,
  };
};

/**
 * Custom hook to access all data from the combined context
 * Provides access to both system and auth tag data
 * Returns default values if used outside of TagAndSystemProvider (graceful degradation)
 */
export const useAuthAndSystemContext = (): AuthAndSystemContextProps => {
  const context = useContext(AuthAndSystemContext);
  if (!context) {
    // Return safe default values when provider is not available
    // This allows components to use the hook without requiring the provider
    console.warn(
      "useAuthAndSystemContext: Hook called outside of TagAndSystemProvider. Returning default values."
    );
    return {
      allSystemData: [],
      isSystemDataLoading: false,
      setAllSystemData: () => {
        console.warn(
          "useAuthAndSystemContext: setAllSystemData called outside of TagAndSystemProvider. This is a no-op."
        );
      },
      allAuthTagsData: [],
      isAuthTagsLoading: false,
      setAllAuthTagsData: () => {
        console.warn(
          "useAuthAndSystemContext: setAllAuthTagsData called outside of TagAndSystemProvider. This is a no-op."
        );
      },
    };
  }
  return context;
};
